import { Component, Input, OnInit, ViewEncapsulation } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';
import { AppNotificationService } from '@core/services/app-notification.service';
import { U2bValidators } from '@shared/validators/validators';
import {
    addDays,
    addMonths,
    addWeeks,
    addYears,
    endOfDay,
    endOfMonth,
    endOfWeek,
    endOfYear,
    startOfDay,
    startOfMonth,
    startOfWeek,
    startOfYear,
    subDays,
    subMonths,
    subWeeks,
    subYears
} from '@core/date.facade';
import { isString } from '@shared/functions/is-string';
import { MatSelectChange } from '@angular/material/select';
import { TolgeeService } from '@core/translation/tolgee.service';

export enum FromToRangeButton {
    All,
    LastYear,
    CurrentYear,
    RestOfYear,
    NextYear,
    LastMonth,
    CurrentMonth,
    RestOfMonth,
    NextMonth,
    LastWeek,
    CurrentWeek,
    RestOfWeek,
    NextWeek,
    Yesterday,
    Today,
    RestOfToday,
    Tomorrow,
}

/** Tolgee keys under `dialogs.dateRangePicker.*` — order must match {@link FromToRangeButton} numeric values. */
const FROM_TO_RANGE_PRESET_KEYS: string[] = [
    'presetAll',
    'presetLastYear',
    'presetCurrentYear',
    'presetRestOfYear',
    'presetNextYear',
    'presetLastMonth',
    'presetCurrentMonth',
    'presetRestOfMonth',
    'presetNextMonth',
    'presetLastWeek',
    'presetCurrentWeek',
    'presetRestOfWeek',
    'presetNextWeek',
    'presetYesterday',
    'presetToday',
    'presetRestOfToday',
    'presetTomorrow',
];

@Component({
    selector: 'add-assignment-dialog',
    templateUrl: './get-from-to-range-dialog.component.html',
    encapsulation: ViewEncapsulation.None,
    standalone: false
})
export class GetFromToRangeDialogComponent implements OnInit {
    dialogTitle: string;

    formGroup: UntypedFormGroup;

    dateToday = new Date();

    @Input() types: FromToRangeButton[];

    @Input() defaultType: FromToRangeButton = FromToRangeButton.All;

    @Input() allowTypeAll = true;

    @Input() fromFieldOptional = true;

    @Input() toFieldOptional = true;

    @Input() fromDateDefault: Date;

    @Input() toDateDefault: Date;

    @Input() fromDateMax: Date;

    @Input() toDateMax: Date;

    @Input() orderByFilters?: { orderBy: string, label: string }[];

    @Input() orderByDefault?: string;

    @Input() optionalInfoText?: string;

    constructor(
        public dialogRef: MatDialogRef<GetFromToRangeDialogComponent>,
        private _appNotificationService: AppNotificationService,
        private _formBuilder: UntypedFormBuilder,
        private _tolgee: TolgeeService,
    ) {
    }

    ngOnInit(): void {
        this._createForm();

        this.types = (this.types || Object.values(FromToRangeButton))
            .filter(_ => !isString(_))
            .filter(_ => this.allowTypeAll || _ !== FromToRangeButton.All) as FromToRangeButton[];
    }

    presetTranslationKey(type: FromToRangeButton): string {
        return `dialogs.dateRangePicker.${FROM_TO_RANGE_PRESET_KEYS[type]}`;
    }

    public save(): void {

        if (this.formGroup.valid) {
            this.dialogRef.close(this.formGroup.value);
            return;
        }

        this.formGroup.markAllAsTouched();
        this._appNotificationService.showError(this._tolgee.translate('common.checkFieldsHighlight'));
    }

    public setDates(event: MatSelectChange): void {
        let from: Date;
        let to: Date;

        switch (event.value as FromToRangeButton) {
            case FromToRangeButton.All:
                from = null;
                to = null;
                break;
            case FromToRangeButton.LastYear:
                from = subYears(startOfYear(this.dateToday), 1);
                to = subYears(endOfYear(this.dateToday), 1);
                break;
            case FromToRangeButton.CurrentYear:
                from = startOfYear(this.dateToday);
                to = endOfYear(this.dateToday);
                break;
            case FromToRangeButton.RestOfYear:
                from = this.dateToday;
                to = endOfYear(this.dateToday);
                break;
            case FromToRangeButton.NextYear:
                from = addYears(startOfYear(this.dateToday), 1);
                to = addYears(endOfYear(this.dateToday), 1);
                break;

            case FromToRangeButton.LastMonth:
                from = subMonths(startOfMonth(this.dateToday), 1);
                to = subMonths(endOfMonth(this.dateToday), 1);
                break;
            case FromToRangeButton.CurrentMonth:
                from = startOfMonth(this.dateToday);
                to = endOfMonth(this.dateToday);
                break;
            case FromToRangeButton.RestOfMonth:
                from = this.dateToday;
                to = endOfMonth(this.dateToday);
                break;
            case FromToRangeButton.NextMonth:
                from = addMonths(startOfMonth(this.dateToday), 1);
                to = addMonths(endOfMonth(this.dateToday), 1);
                break;

            case FromToRangeButton.LastWeek:
                from = subWeeks(startOfWeek(this.dateToday), 1);
                to = subWeeks(endOfWeek(this.dateToday), 1);
                break;
            case FromToRangeButton.CurrentWeek:
                from = startOfWeek(this.dateToday);
                to = endOfWeek(this.dateToday);
                break;
            case FromToRangeButton.RestOfWeek:
                from = this.dateToday;
                to = endOfWeek(this.dateToday);
                break;
            case FromToRangeButton.NextWeek:
                from = addWeeks(startOfWeek(this.dateToday), 1);
                to = addWeeks(endOfWeek(this.dateToday), 1);
                break;

            case FromToRangeButton.Yesterday:
                from = subDays(startOfDay(this.dateToday), 1);
                to = subDays(endOfDay(this.dateToday), 1);
                break;
            case FromToRangeButton.Today:
                from = startOfDay(this.dateToday);
                to = endOfDay(this.dateToday);
                break;
            case FromToRangeButton.RestOfToday:
                from = this.dateToday;
                to = endOfDay(this.dateToday);
                break;
            case FromToRangeButton.Tomorrow:
                from = addDays(startOfDay(this.dateToday), 1);
                to = addDays(endOfDay(this.dateToday), 1);
                break;
        }

        this.formGroup.patchValue({from, to});
    }

    private _createForm(): void {
        this.formGroup = this._formBuilder.group({
                from: [this.fromDateDefault, this.fromFieldOptional
                    ? undefined
                    : [U2bValidators.required(this._tolgee.translate('common.validationMsg.dateFromRequired'))]],
                to: [this.toDateDefault, this.toFieldOptional
                    ? undefined
                    : [U2bValidators.required(this._tolgee.translate('common.validationMsg.dateToRequired'))]],
                orderBy: [this.orderByDefault],
            },
            {
                validators: [
                    (formGroup: UntypedFormGroup) => {

                        const fromDate = formGroup.get('from').value;
                        const toDate = formGroup.get('to').value;

                        if (!fromDate || !toDate) {
                            return null;
                        }

                        if (fromDate > toDate) {
                            formGroup.get('from').setErrors({
                                wrongDateRange: {
                                    message: this._tolgee.translate('dialogs.booking.berth.fromAfterToError')
                                }
                            });
                            formGroup.get('to').setErrors({
                                wrongDateRange: {
                                    message: this._tolgee.translate('dialogs.booking.berth.toBeforeFromError')
                                }
                            });
                        } else {
                            formGroup.get('from').setErrors(null);
                            formGroup.get('to').setErrors(null);
                        }
                    }
                ]
            }
        );
    }
}
